// dealoftheday.ts (or dealOfTheDayApi.ts)
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const dealOfTheDayApi = createApi({
  reducerPath: 'dealOfTheDayApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set(
        'authkey',
        '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3'
      );
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['DealOfTheDay'],
  endpoints: (build) => ({
    // List all deals of the day
    listDealsOfTheDay: build.query({
      query: () => ({
        url: 'user/listdeal',
        method: 'POST',
        body: {},
      }),
      providesTags: ['DealOfTheDay'],
      // Transform response to ensure proper data structure and calculate status
      transformResponse: (response: any) => {
        console.log('List Deals API Response:', response);
        
        if (response?.data) {
          const now = new Date();
          
          // Update status based on current date/time
          const updatedDeals = response.data.map((deal: any) => {
            const startDate = new Date(deal.start_date);
            const endDate = new Date(deal.end_date);
            
            // Calculate automatic status based on dates
            let calculatedStatus = deal.status; // Default to stored status
            
            // If deal is manually set to active (1), check if it's within date range
            if (deal.status === 1) {
              calculatedStatus = (now >= startDate && now <= endDate) ? 1 : 0;
            }
            // If deal is manually inactive (0), keep it inactive regardless of dates
            
            return {
              ...deal,
              calculated_status: calculatedStatus, // Add calculated status
              is_active_by_date: (now >= startDate && now <= endDate) // Add date-based active flag
            };
          });
          
          response.data = updatedDeals;
          
          // Log each deal to check ID and status
          response.data.forEach((deal: any) => {
            console.log('Deal:', {
              id: deal.id,
              deal_id: deal.deal_id,
              product_id: deal.product_id,
              stored_status: deal.status,
              calculated_status: deal.calculated_status,
              is_active_by_date: deal.is_active_by_date
            });
          });
        }
        return response;
      },
    }),
    
    // Add new deal
    addDeal: build.mutation({
      query: (dealData) => {
        const payload = {
          status: Number(dealData.status),
          discount: Number(dealData.discount),
          start_date: dealData.start_date,
          end_date: dealData.end_date,
          product_id: Number(dealData.product_id),
        };

        console.log('Add Deal Payload:', payload);
        
        return {
          url: 'user/adddeal',
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: ['DealOfTheDay'],
    }),
    
    // Update existing deal
    updateDeal: build.mutation({
      query: (dealData) => {
        const payload = {
          id: Number(dealData.id),
          status: Number(dealData.status),
          discount: Number(dealData.discount),
          start_date: dealData.start_date,
          end_date: dealData.end_date,
          product_id: Number(dealData.product_id),
        };

        console.log('Update Deal Payload:', payload);
        
        return {
          url: 'user/updatedeal',
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: ['DealOfTheDay'],
    }),
    
    // Delete deal of the day
    deleteDealOfTheDay: build.mutation({
      query: (id) => {
        console.log('Delete Deal - Received ID:', id, 'Type:', typeof id);
        
        // Ensure id is a valid number
        const dealId = Number(id);
        
        if (!dealId || isNaN(dealId)) {
          console.error('Invalid deal ID provided:', id);
          throw new Error(`Invalid deal ID: ${id}`);
        }
        
        // API expects 'deal_id' parameter, not 'id'
        const payload = { deal_id: dealId };
        console.log('Delete Deal Payload:', payload);
        
        return {
          url: 'user/deletedeal',
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: ['DealOfTheDay'],
      // Transform error response for better debugging
      transformErrorResponse: (response: any) => {
        console.error('Delete API Error Response:', response);
        return response;
      },
    }),
  }),
});

export const {
  useListDealsOfTheDayQuery,
  useAddDealMutation,
  useUpdateDealMutation,
  useDeleteDealOfTheDayMutation,
} = dealOfTheDayApi;